/**
 * CHG-052 REQ-V52-019: 스킴 선택 모달 (마이그레이션 미리보기 포함)
 */
import { useState, useEffect } from 'react';
import { X, AlertTriangle, Check } from 'lucide-react';
import { useSchemes } from '../../hooks/useIssueTypeSchemeAdmin';
import { useMigrationPreview } from '../../hooks/useProjectIssueTypeConfig';
import { DynamicIcon } from '../ui/DynamicIcon';
import type { MigrationPreviewResult } from '../../services/projectIssueTypeConfigService';

interface Props {
  projectId: string;
  currentSchemeId?: string;
  onSelect: (schemeId: string) => Promise<void>;
  onClose: () => void;
  applying?: boolean;
}

export default function SchemeSelectModal({ projectId, currentSchemeId, onSelect, onClose, applying }: Props) {
  const { data: schemes } = useSchemes();
  const previewMutation = useMigrationPreview();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<MigrationPreviewResult | null>(null);

  // 선택 변경 시 미리보기 실행
  useEffect(() => {
    if (!selectedId || selectedId === currentSchemeId) {
      setPreview(null);
      return;
    }
    previewMutation.mutate(
      { projectId, opts: { newSchemeId: selectedId } },
      { onSuccess: (data) => setPreview(data) },
    );
  }, [selectedId]);

  const handleApply = async () => {
    if (!selectedId) return;
    await onSelect(selectedId);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <h3 className="text-base font-semibold text-gray-900">스킴 선택</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="size-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1 space-y-2">
          {schemes?.map((s) => {
            const isSelected = selectedId === s.schemeId;
            const isCurrent = currentSchemeId === s.schemeId;
            return (
              <button
                key={s.schemeId}
                onClick={() => setSelectedId(s.schemeId)}
                className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : isCurrent
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-gray-900">{s.name}</span>
                  <div className="flex items-center gap-1.5">
                    {isCurrent && (
                      <span className="text-[11px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">
                        현재
                      </span>
                    )}
                    {isSelected && <Check size={16} className="text-blue-600" />}
                  </div>
                </div>
                {s.description && (
                  <p className="text-sm text-gray-500 mb-2 truncate">{s.description}</p>
                )}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {s.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 rounded text-sm text-gray-600"
                    >
                      <DynamicIcon name={item.issueType.icon} color={item.issueType.color} customIconPath={item.issueType.customIconPath} size={10} />
                      {item.issueType.name}
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        {/* Preview */}
        {preview && !preview.canProceed && (
          <div className="px-4 pb-2 shrink-0">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle size={14} className="text-amber-600" />
                <span className="text-sm font-medium text-amber-700">
                  영향받는 이슈 {preview.totalAffected}건
                </span>
              </div>
              <div className="space-y-1">
                {preview.affectedIssues.map((a) => (
                  <p key={a.issueTypeId} className="text-sm text-amber-600">
                    {a.issueTypeName}: {a.count}건
                    {a.issues.length > 0 && (
                      <span className="text-amber-500"> ({a.issues.map((i) => i.issueId).join(', ')})</span>
                    )}
                  </p>
                ))}
              </div>
              <p className="text-sm text-amber-600 mt-1.5">
                먼저 해당 이슈들의 타입을 변경해주세요.
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleApply}
            disabled={!selectedId || selectedId === currentSchemeId || applying || (preview !== null && !preview.canProceed)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applying ? '적용 중...' : '적용'}
          </button>
        </div>
      </div>
    </div>
  );
}
